import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';

import { z } from 'zod';

import { FrontendTool, provideTellentFrontendTool } from '@core/tellent-assistant-contracts';

/**
 * One allowed route the agent may navigate to. `path` may include `:name`
 * placeholders; for each placeholder, `params` must declare a description
 * telling the agent what value to supply.
 */
export interface NavigateRoute {
  /** Path or path template. Placeholders take the form `:name` and must have
   *  a matching entry in `params`. Example: `/candidates/:id`. */
  readonly path: string;
  /** Human-facing label surfaced in the tool description; helps the agent
   *  pick the right route from a natural-language request. */
  readonly label: string;
  /** Per-placeholder descriptions. Keys match `:name` placeholders in `path`.
   *  Values are free-form guidance the agent reads to decide what to pass
   *  (e.g. "Candidate GUID (uuid)"). Omit for non-parametric routes. */
  readonly params?: Readonly<Record<string, string>>;
}

const outputSchema = z.object({ success: z.boolean(), reason: z.string().optional() });

type NavigateOutput = z.infer<typeof outputSchema>;

interface NavigateInput {
  to: string;
  params?: Record<string, string>;
}

/**
 * Registers a `navigate` frontend tool bound to the host app's Router. Agents
 * can move the user between named routes in response to conversational
 * requests. The catalog is caller-configured; unknown templates short-circuit
 * with `{ success: false, reason: 'route not allowed' }`.
 */
export function provideTellentNavigateTool(config: {
  /** Exhaustive catalog of path templates the agent may pass as `to`. */
  readonly allowedRoutes: readonly NavigateRoute[];
}): EnvironmentProviders {
  validateCatalog(config.allowedRoutes);
  return makeEnvironmentProviders([
    provideTellentFrontendTool({
      useFactory: (router: Router): FrontendTool<NavigateInput, NavigateOutput> =>
        createNavigateTool(router, config.allowedRoutes),
      deps: [Router],
    }),
  ]);
}

const placeholderPattern = /:([A-Za-z_][A-Za-z0-9_]*)/g;

function extractPlaceholders(path: string): string[] {
  return [...path.matchAll(placeholderPattern)].map(m => m[1]);
}

function validateCatalog(routes: readonly NavigateRoute[]): void {
  if (routes.length === 0) {
    throw new Error('navigate: allowedRoutes must not be empty');
  }
  for (const r of routes) {
    const declared = new Set(Object.keys(r.params ?? {}));
    const used = new Set(extractPlaceholders(r.path));
    for (const p of used) {
      if (!declared.has(p)) {
        throw new Error(`navigate: route "${r.path}" uses :${p} but params.${p} is missing`);
      }
    }
    for (const p of declared) {
      if (!used.has(p)) {
        throw new Error(`navigate: route "${r.path}" declares params.${p} but path has no :${p}`);
      }
    }
  }
}

function createNavigateTool(
  router: Router,
  allowedRoutes: readonly NavigateRoute[],
): FrontendTool<NavigateInput, NavigateOutput> {
  const byPath = new Map(allowedRoutes.map(r => [r.path, r]));
  const allowedPaths = allowedRoutes.map(r => r.path);
  const inputSchema = z.object({
    to: z.enum(<[string, ...string[]]>allowedPaths),
    params: z.record(z.string(), z.string()).optional(),
  });
  const catalog = allowedRoutes.map(describeRoute).join('\n');

  return {
    name: 'navigate',
    description: `Navigate the user to one of the allowed routes. Pick the path whose label matches the user's intent. For paths containing ":name" placeholders, pass values in the params object.\n\nAllowed routes:\n${catalog}`,
    parameters: {
      type: 'object',
      properties: {
        to: { type: 'string', enum: allowedPaths, description: 'Exact allowed path template.' },
        params: {
          type: 'object',
          additionalProperties: { type: 'string' },
          description: 'Values for :name placeholders in `to`. Omit for non-parametric routes.',
        },
      },
      required: ['to'],
    },
    input: inputSchema,
    output: outputSchema,
    execute: async ({ to, params }) => {
      const route = byPath.get(to);
      if (!route) return { success: false, reason: 'route not allowed' };
      const resolution = resolvePath(route, params ?? {});
      if ('reason' in resolution) return { success: false, reason: resolution.reason };
      const ok = await router.navigateByUrl(resolution.url);
      return { success: ok, reason: ok ? undefined : 'navigation failed' };
    },
  };
}

function describeRoute(r: NavigateRoute): string {
  const paramsLine = r.params
    ? '\n    params: ' +
      Object.entries(r.params)
        .map(([k, d]) => `${k} (${d})`)
        .join(', ')
    : '';
  return `  - "${r.path}" — ${r.label}${paramsLine}`;
}

function resolvePath(
  route: NavigateRoute,
  supplied: Record<string, string>,
): { url: string } | { reason: string } {
  const placeholders = extractPlaceholders(route.path);
  for (const name of placeholders) {
    const value = supplied[name];
    if (!value) return { reason: `missing params.${name}` };
  }
  const unknown = Object.keys(supplied).filter(k => !placeholders.includes(k));
  if (unknown.length > 0) return { reason: `unexpected params: ${unknown.join(', ')}` };
  let url = route.path;
  for (const name of placeholders) {
    url = url.replace(`:${name}`, encodeURIComponent(supplied[name]));
  }
  return { url };
}
